import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Download, MessageSquare, Headphones, Loader2, MapPin, Calendar, Clock, Car, User, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { gbp } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Confirmation() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);

  useEffect(() => { api.get(`/bookings/ref/${ref}`).then(({ data }) => setB(data)).catch(() => {}); }, [ref]);

  if (!b) return <div className="flex h-96 items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;

  const contactDriver = async () => {
    if (!user) { toast.info("Please log in to message your driver"); navigate("/login"); return; }
    try {
      const { data } = await api.post("/conversations", { type: "driver", booking_ref: b.booking_ref, subject: `Driver chat · ${b.booking_ref}` });
      navigate(`/messages?c=${data.id}`);
    } catch { toast.error("Could not start chat"); }
  };
  const contactSupport = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/conversations", { type: "support", booking_ref: b.booking_ref, subject: `Support · ${b.booking_ref}` });
      navigate(`/messages?c=${data.id}`);
    } catch { toast.error("Could not start chat"); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <CheckCircle2 size={64} className="mx-auto text-emerald-500" />
        <h1 className="mt-4 font-heading text-3xl font-bold text-navy">Booking Confirmed</h1>
        <p className="mt-2 text-slate-500">Your driver has been assigned. A confirmation has been sent to your account.</p>
        <div className="mt-4 inline-block rounded-full bg-brand-light px-4 py-1.5 font-heading text-lg font-bold text-brand" data-testid="confirmation-ref">{b.booking_ref}</div>

        <div className="mt-8 grid gap-3 text-left sm:grid-cols-2">
          <Info icon={MapPin} label="Pickup" value={b.pickup?.name} />
          {b.dropoff && <Info icon={MapPin} label="Destination" value={b.dropoff.name} />}
          <Info icon={Calendar} label="Date" value={b.date} />
          <Info icon={Clock} label="Time" value={b.time} />
          <Info icon={Car} label="Vehicle" value={b.vehicle_label} />
          <Info icon={User} label="Driver / Operator" value={b.driver_name ? `${b.driver_name} · ${b.operator_name}` : b.operator_name} />
        </div>

        <div className="mt-6 flex items-center justify-between rounded-xl bg-slate-50 px-5 py-4">
          <span className="font-heading font-semibold text-navy">Total paid</span>
          <span className="font-heading text-2xl font-bold text-navy">{gbp(b.total)}</span>
        </div>

        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <Button variant="outline" onClick={() => user ? navigate("/dashboard") : navigate("/login")} data-testid="view-booking-btn">View Booking</Button>
          <Button className="gap-2 bg-brand hover:bg-brand/90 text-white" onClick={() => user ? navigate(`/track/${b.id}`) : navigate("/login")} data-testid="track-ride-btn"><Navigation size={15} /> Track My Ride</Button>
          <Button variant="outline" className="gap-2" onClick={() => toast.success("Receipt downloaded (demo)")} data-testid="download-receipt-btn"><Download size={15} /> Download Receipt</Button>
          <Button variant="outline" className="gap-2" onClick={contactDriver} data-testid="contact-driver-btn"><MessageSquare size={15} /> Contact Driver</Button>
          <Button variant="outline" className="gap-2" onClick={contactSupport} data-testid="contact-support-btn"><Headphones size={15} /> Contact Support</Button>
        </div>
      </div>
    </div>
  );
}
function Info({ icon: Icon, label, value }) {
  return <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-3">
    <Icon size={16} className="mt-0.5 text-brand" />
    <span><span className="block text-xs text-slate-400">{label}</span><span className="text-sm font-medium text-navy">{value}</span></span>
  </div>;
}
